/**
 * Utilidades de formateo centralizadas.
 *
 * Formato monetario español: separador de millares (punto), decimales (coma)
 * y símbolo de euro. Ej.: 5.000,00 €
 */

/**
 * Formatea un importe como moneda en formato español (EUR).
 *
 * @param value    Importe (number o string numérico). null/undefined/NaN -> "-".
 * @param decimals Nº de decimales (por defecto 2). Usar 0 para importes en euros enteros.
 */
export const formatCurrency = (
  value?: number | string | null,
  decimals: number = 2
): string => {
  if (value === undefined || value === null || value === '') return '-';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    // Forzar separador de millares también en 1.000–9.999: el locale español
    // (minimumGroupingDigits=2) no agrupa esos por defecto (mostraría "5000,00 €").
    // 'always' lo soporta ICU en runtime; el cast evita el tipo antiguo (boolean).
    useGrouping: 'always' as unknown as boolean,
  }).format(n);
};

/**
 * Convierte lo que el usuario escribe en un campo de importe a número.
 * Formato español: los puntos son separadores de millares y la coma es decimal.
 * Ej.: "50.000,00" -> 50000 ; "50000" -> 50000 ; "" -> undefined.
 */
export const parseCurrencyInput = (str: string): number | undefined => {
  if (str == null) return undefined;
  const cleaned = str.replace(/[^\d,-]/g, '').replace(',', '.');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return undefined;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? undefined : n;
};

/**
 * Formatea un número para mostrarlo en un campo de importe (sin símbolo de euro),
 * con separador de millares y dos decimales. Ej.: 50000 -> "50.000,00".
 */
export const formatCurrencyInput = (value?: number | null): string => {
  if (value == null || (typeof value === 'number' && Number.isNaN(value))) return '';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always' as unknown as boolean,
  }).format(value);
};
